import sanitizeHtml from 'sanitize-html';
import { merge } from 'lodash-es';

// Default sanitization configuration extracted from EmailEvent component
const defaultConfig = {
  allowedTags: [
    'b', 'strong', 'i', 'em', 'a', 'br', 'ul', 'ol', 'li', 'p', 'div', 'blockquote', 'u', 'span',
  ],
  allowedAttributes: {
    a: ['href', 'target', 'rel'],
    div: ['class', 'dir'],
    blockquote: ['class'],
    i: ['class'],
  },
  allowedSchemes: ['http', 'https', 'mailto'],
  transformTags: {
    strong: (tagName, attribs) => ({
      tagName: 'b',
      attribs,
    }),
    i: (tagName, attribs) => ({
      tagName: 'i',
      attribs: {
        ...attribs,
        class: 'italic',
      },
    }),
    a: (tagName, attribs) => {
      let href = attribs.href || '';
      if (!href) {
        return { tagName: 'span', attribs };
      }
      // Check for javascript: URLs in a safer way
      if (href.match(/^\s*javascript:/i)) {
        href = '#';
      } else if (href && !href.startsWith('#') && !href.startsWith('mailto:') && !href.match(/^\s*https:\/\//i)) {
        href = `https://${href}`;
      }

      return {
        tagName,
        attribs: {
          ...attribs,
          href,
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      };
    },
    blockquote: (tagName, attribs) => ({
      tagName,
      attribs: {
        ...attribs,
        class: 'bg-black/3 p-2 mt-4',
      },
    }),
    div: (tagName, attribs) => {
      const className = attribs.class || '';

      if (className.includes('gmail_quote_container')) {
        return {
          tagName: 'div',
          attribs: {
            ...attribs,
            class: 'bg-black/5 p-4 mt-4 rounded-md',
          },
        };
      }

      return { tagName: 'div', attribs };
    },
  },
  textFilter(text) {
    return text
      .replace(/[\u200B-\u200F\u2060-\u206F\uFEFF\u00AD]/g, '')
      .replace(/\u200C|\u034F|\u061C|\u115F|\u1160|\u17B4|\u17B5|\u180E|\u2000-\u200F|\u202F|\u205F|\u2800|\u3000|\uFEFF/g, '');
  },
};

/**
 * Sanitizes HTML content with a comprehensive default configuration
 * @param {string} html - The HTML content to sanitize
 * @param {Object} configOverrides - Partial configuration to override defaults
 * @returns {string} - The sanitized HTML
 */
export function sanitizeHtmlContent(html, configOverrides = {}) {
  if (!html) return '';

  // Deep merge the default config with any overrides
  const config = merge({}, defaultConfig, configOverrides);

  let sanitized = sanitizeHtml(html, config);

  // Apply post-processing transformations
  // Replace sequences of more than 2 consecutive <br> tags with just 2 <br> tags
  sanitized = sanitized.replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>');

  // Add space after closing tags (except for already spaced ones)
  sanitized = sanitized.replace(/>((?!\s))/g, '> $1');

  return sanitized;
}

export default sanitizeHtmlContent;
